import { AdverseEventForm, EventDefinitionType, Permission, VisitDetailsForm as VisitDetailsFormDto } from "@/api/dtos";
import { FormPatientDetails } from "@/components/common/PatientDetails";
import useBaselineWeightByPatient from "@/hooks/data/useBaselineWeightByPatient";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { positiveWithPrecision, withPrecision } from "@/utils/NumberFormats";
import { Box, Divider, Typography } from "@mui/material";
import { ConditionMode, useFormActions, useFormState, ValidateOn } from "@ngt/forms-core";
import { Checkbox, CollaboratingGroupContext, CrfCondition, CrfForm, CtcaeSelector, CtcaeSelectorType, DatePicker, FormBreadcrumbs, FormDefinitionsContext, FormLabelsContext, FormLookupsContext, GroupedField, GroupedTableArrayField, ICrfConditionParameters, IGroupedTableArrayFieldColumn, Input, InstitutionContext, MasterGroupContext, Numeric, PageLayout, PatientContext, RouteLoading, Select, ToggleButtonGroup, useFormLabels, useFormLookups, useFormMetadata, useLookups } from "@ngt/opms";
import { usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo } from "react";
import { OngoingUpdater } from "./AdverseEventsForm";
interface IVisitDetailsFormProps {

}

interface IFormContentProps {

}

const resolutionOngoing = (index: number) => ({ formState }: ICrfConditionParameters<VisitDetailsFormDto>) => { 
    const ongoing = formState?.value?.adverseEventsForm?.adverseEventForms?.[index]?.ongoing;

    return !ongoing;
};

const AdverseEventColumns: Array<IGroupedTableArrayFieldColumn<AdverseEventForm>> = [
    {
        propertyPath: 'ctcaeTermId',
        TableCellProps: {
            sx: {
                minWidth: 450,
                maxWidth: 450,
                width: 450,
            }
        },
        TableCellContentProps: {
            sx: {
                display:"flex",
                alignItems: "center"
            }
        },
        content: (
            <Input
                component={CtcaeSelector}
                gradeFieldName="ctcaeGradeId"
                specificationFieldName="ctcaeSpecification"
                categoryFieldName="ctcaeCategoryId"
                type={CtcaeSelectorType.SelectorWithName}
                ctcaeUrl="https://ctcae.breastcancertrials.org.au/"
            />
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'onsetDate',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            }
        },
        content: (
            // <Input
            //     component={Masked}
            //     maskedFormat={UK_DATE}
            // />
            <Input 
                component={DatePicker}
                format={"dd-MMM-yyyy"}
            />
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'resolutionDate',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            },
        },
        rowAwareContent: (_parentPropertyPath, index) => (
            // <Input
            //     component={Masked}
            //     maskedFormat={UK_DATE}
            // />
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={resolutionOngoing(index)}
                subscription={{ value: true }}
            >
                <Input 
                    component={DatePicker}
                    format={"dd-MMM-yyyy"}
                />
            </CrfCondition>
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'ongoing',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
                justifyContent: 'center',
                pr: 3
            },
        },
        TableCellContentProps: {
            sx: {
                display:"flex",
                alignItems: "center"
            }
        },
        content: (
            <>
                <OngoingUpdater />
                <Input
                    component={Checkbox}
                    label=""
                />
            </>
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'relatedness',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            }
        },
        content: (
            <>
                <Input
                    component={Select}
                />
            </>
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'actionTaken',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            }
        },
        content: (
            <>
                <Input
                    component={Select}
                />
            </>
        ),
        reserveFlagSpace: false
    },
];


const CalculatedFieldContent: FC<IFormContentProps> = () => {
    const { setFieldValue } = useFormActions();
    const { value } = useFormState({ value : true});
    const {data: patient} = useContext(PatientContext);
    const {data: baselineWeight} = useBaselineWeightByPatient(patient?.id);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            if(!value.weight || !baselineWeight) {
                setFieldValue("weightDifference", undefined);
                return;
            }
            
            setFieldValue("weightDifference", value.weight - baselineWeight);
        }, 500)

        return () => window.clearTimeout(timeout);
    }, [baselineWeight, value.weight]);

    return null;
}

const FormContextEditor: FC<PropsWithChildren> = ({
    children
}) => {
    const formMetadata = useFormMetadata("adverse-events");
    const lookupContext = useLookups(formMetadata);
    const formLookups = useFormLookups(lookupContext.data);
    const { data: formDefinitions }  = useContext(FormDefinitionsContext);

    const formDefinition = useMemo(() => (
        formDefinitions?.find(x => x.code == formMetadata?.formDefinitionCode)
    ), [formDefinitions, formMetadata?.formDefinitionCode]);

    const formLabels = useFormLabels(formDefinition);

    const formLookupsContext = useContext(FormLookupsContext);
    const formLabelsContext = useContext(FormLabelsContext);

    const newLookupsContext: ReturnType<typeof useFormLookups> = useMemo(() => [
        ...(formLookupsContext ?? []),
        ...(formLookups?.map(x => ({...x, propertyName: `adverseEventsForm.${x.propertyName}`})) ?? [])
    ], [formLookupsContext, formLookups]);

    const newLabelsContext: ReturnType<typeof useFormLabels> = useMemo(() => [
        ...(formLabelsContext),
        ...(formLabels?.map(x => ({...x, propertyPath: `adverseEventsForm.${x.propertyPath}`})) ?? [])
    ], [formLabelsContext, formLabels]);
    
    return (
        <FormLookupsContext.Provider value={newLookupsContext}>
            <FormLabelsContext.Provider value={newLabelsContext}>
                {children}
            </FormLabelsContext.Provider>
        </FormLookupsContext.Provider>
    )
}

const VisitDetailsForm: FC<IVisitDetailsFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    
    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );
    
    const cycle2 = useCallback((params: ICrfConditionParameters<VisitDetailsFormDto>) => 
        params.eventDefinition?.id == EventDefinitionType.Cycle2
    , [])

    const cycle2Or3 = useCallback((params: ICrfConditionParameters<VisitDetailsFormDto>) => 
        params.eventDefinition?.id == EventDefinitionType.Cycle2 ||
        params.eventDefinition?.id == EventDefinitionType.Cycle3
    , [])


    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm 
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
                showEventHeading
            >
                <FormPatientDetails />
                <FormContextEditor>
                    <CalculatedFieldContent />
                    <GroupedField
                        propertyPath="visitDate"
                        format="dd-MMM-yyyy"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                    <CrfCondition
                        condition={cycle2Or3}
                        mode={ConditionMode.Show}
                    >
                        <GroupedField
                            propertyPath="administrationDate"
                            format="dd-MMM-yyyy"
                            component={DatePicker}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                    <GroupedField 
                        propertyPath="weight"
                        component={Numeric}
                        numberFormat={positiveWithPrecision(1)}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                    <GroupedField
                        propertyPath="weightDifference"
                        component={Numeric}
                        numberFormat={withPrecision(1)}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                        disabled
                    />
                    <CrfCondition
                        condition={cycle2}
                        mode={ConditionMode.Show}
                    >
                        <GroupedField
                            propertyPath="dateStartTdxD"
                            component={DatePicker}
                            format={"dd-MMM-yyyy"}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                    <GroupedField 
                        propertyPath={"newAdverseEvents"}
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />

                    <Divider />
                    <Box
                        sx={{
                            mt: 2,
                            pl: 2,
                            mb: 2,
                        }}
                    >
                        <Typography 
                            variant="h2"
                        >
                            Adverse Events
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            pl: 2,
                            pb: 2,
                        }}
                    >
                        <Typography>
                            All Adverse Events are to be reported as per NCI-CTCAE v5.0 from the date main 
                            informed consent is signed until Cycle 3 Day 1, or until T-DXd is permanently ceased. 
                            From Cycle 3 Day 1 onwards, only Adverse Events that result in treatment permanently 
                            ceasing are to be reported.
                        </Typography>
                    </Box>
                    <GroupedTableArrayField
                        propertyPath="adverseEventsForm.adverseEventForms"
                        columns={AdverseEventColumns}
                        entityName="adverse event"
                    />
                </FormContextEditor>
            </CrfForm>
        </PageLayout>
    );
}

export default VisitDetailsForm;